"use client";

import { onPayment } from "@web/components/PaymentClose";
import config from "@web/data/config.json";
import { useRefreshMetadata } from "@web/data/metadata";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Price, useAccount, useAccountToken } from "./auth";

const BalanceContext = createContext<{
  balance: Price | undefined;
  refresh: () => Promise<void>;
}>({
  balance: {
    value: 0,
    currency: "USD",
  },
  refresh() {
    return Promise.resolve();
  },
});

export interface BalanceUpdate {
  value: number;
  currency: string;
}

let balanceChangeHandlers: ((balance: Price) => boolean | void)[] = [];

export function addBalanceChangeHandler(
  handler: (balance: Price) => boolean | void,
) {
  balanceChangeHandlers.push(handler);
}

export function BalanceProvider({ children }: { children: ReactNode }) {
  const account = useAccount();
  const [balance, setBalance] = useState<Price | undefined>(account?.balance);
  const token = useAccountToken();

  const refresh = useCallback(
    () =>
      fetch(config.API_URL + "account/balance/value", {
        headers: {
          authorization: `Bearer ${token}`,
        },
      })
        .then((result) => {
          if (!result.ok) {
            throw new Error("Invalid balance");
          }
          return result.json();
        })
        .then((result) => {
          setBalance((previous) =>
            typeof result === "number"
              ? { value: result, currency: "USD" }
              : result,
          );
        }),
    [token],
  );

  const refreshMetadata = useRefreshMetadata();
  useEffect(() => {
    return onPayment((status) => {
      if (status === "success") {
        refresh();
        refreshMetadata();
      }
    });
  }, [refresh, refreshMetadata]);

  const contextValue = useMemo(
    () => ({ balance, refresh }),
    [balance, refresh],
  );

  return (
    <BalanceContext.Provider value={contextValue}>
      {children}
    </BalanceContext.Provider>
  );
}

export function useBalance() {
  const { balance } = useContext(BalanceContext);
  return useMemo(
    () => ({
      symbol: getCurrencySymbolFromCode(balance?.currency),
      balance: -(balance?.value ?? 0),
    }),
    [balance?.currency, balance?.value],
  );
}

export function useRefreshBalance() {
  const { refresh } = useContext(BalanceContext);
  return refresh;
}

export function getCurrencySymbolFromCode(code: string | undefined) {
  if (!code) {
    return "$";
  }

  const normalized = code.replace(/[^a-z]/gi, "").toLowerCase();

  switch (normalized) {
    case "usd":
      return "$";
    case "eur":
      return "$";
    default:
      return "$";
  }
}
